// public-reader/services/publicBooksApi.ts — typed fetch client for the
// unauthenticated public-book endpoints under /api/public/books.
//
// All routes here are CORS-permissive (no bearer auth required). The fork
// route is the exception — it pulls bearer auth via authedFetch from
// auth/tokens.ts, but we keep it next to the rest of the public surface
// so the public-reader UI has one import for everything it talks to.

import { API_BASE, authedFetch } from "../../auth/tokens";

export interface PublicBookChapter {
  id: string;
  chapterNumber: number;
  title: string;
  /**
   * Chapter body markdown. `null` when this chapter wasn't shipped
   * inline — Phase C: `GET /:slug?lang=X` only inlines chapter 1 when
   * translation is active; chapters 2..N return `bodyMd: null` and the
   * client lazy-fetches them via `GET /:slug/chapters/:n?lang=X`.
   */
  bodyMd: string | null;
  startTurn: number;
  endTurn: number;
  status: string;
  generatedAt: string;
  /** True when this chapter's title/body have been translated. */
  translated?: boolean;
}

export interface PublicBookLineageEntry {
  storyId: string;
  storyName: string;
  chapterNumber: number;
  userId: string;
  userName: string;
}

export interface PublicBookResponse {
  slug: string;
  title: string;
  author: { id: string; name: string };
  scenario: { id: string; name: string };
  status: "shared" | "listed" | "revoked";
  allowContinuation: boolean;
  allowEpub: boolean;
  customSubtitle: string | null;
  publishedAt: string;
  /** The story's source language (BCP-47). Locked at creation time. */
  originalLang: string;
  /** The language this response is rendered in. Equals originalLang when no translation is in play. */
  lang: string;
  ogImageUrl: string;
  lineage: PublicBookLineageEntry[];
  chapters: PublicBookChapter[];
}

/**
 * Per-chapter response shape from `GET /:slug/chapters/:n?lang=X`. Used by
 * the speculative-prefetch + explicit-load paths in the Phase C reader.
 */
export interface PublicChapterResponse {
  id: string;
  chapterNumber: number;
  title: string;
  bodyMd: string;
  startTurn: number;
  endTurn: number;
  status: string;
  generatedAt: string;
  originalLang: string;
  lang: string;
  translated: boolean;
}

export class PublicBooksApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "PublicBooksApiError";
    this.status = status;
    this.code = code;
  }
}

async function parseOrThrow<T>(res: Response): Promise<T> {
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON body — keep text for the error message.
    }
  }
  if (!res.ok) {
    const errBody = (body as { error?: string; code?: string }) || {};
    throw new PublicBooksApiError(
      errBody.error || (text ? text.slice(0, 200) : `HTTP ${res.status}`),
      res.status,
      errBody.code,
    );
  }
  return body as T;
}

/**
 * Load a public book by slug. No bearer header — this is the open reader.
 * Also fires the server-side attribution side-effect (sets eh_ref cookie,
 * writes a referral_clicks row).
 *
 * Phase C: optional `lang` switches the response to translated mode. The
 * server inlines chapter 1's body translated and returns chapters 2..N
 * with `bodyMd: null` — the client lazy-fetches via `fetchPublicChapter`.
 */
export async function loadPublicBook(
  slug: string,
  lang?: string,
): Promise<PublicBookResponse> {
  const url =
    `${API_BASE}/api/public/books/${encodeURIComponent(slug)}` +
    (lang ? `?lang=${encodeURIComponent(lang)}` : "");
  const res = await fetch(url, {
    method: "GET",
    // Cookies need to ride along so the eh_ref Set-Cookie propagates back
    // and is sent on the subsequent /track + signup calls.
    credentials: "include",
  });
  return parseOrThrow<PublicBookResponse>(res);
}

/**
 * Fetch a single chapter by chapter-number. When `lang` differs from the
 * book's original language, the server translates on miss (caches the
 * result for subsequent fetches). Same-language requests return the source
 * chapter verbatim.
 */
export async function fetchPublicChapter(
  slug: string,
  chapterNumber: number,
  lang?: string,
): Promise<PublicChapterResponse> {
  const url =
    `${API_BASE}/api/public/books/${encodeURIComponent(slug)}/chapters/${encodeURIComponent(
      String(chapterNumber),
    )}` + (lang ? `?lang=${encodeURIComponent(lang)}` : "");
  const res = await fetch(url, {
    method: "GET",
    credentials: "include",
  });
  return parseOrThrow<PublicChapterResponse>(res);
}

/**
 * Fire-and-forget prefetch of the next chapter. The response body is
 * thrown away — the point is the server-side cache write, so the next
 * navigation finds the translation already in `chapter_translations`.
 *
 * Errors are swallowed deliberately: a failed prefetch is invisible to
 * the user and the real fetch happens later anyway.
 */
export function prefetchPublicChapter(
  slug: string,
  chapterNumber: number,
  lang?: string,
): void {
  const url =
    `${API_BASE}/api/public/books/${encodeURIComponent(slug)}/chapters/${encodeURIComponent(
      String(chapterNumber),
    )}` + (lang ? `?lang=${encodeURIComponent(lang)}` : "");
  fetch(url, { method: "GET", credentials: "include" }).catch(() => {
    // Swallow — prefetch is best-effort.
  });
}

export interface ForkResponse {
  newStoryId: string;
  forkedFromChapter: number;
}

/**
 * POST /api/public/books/:slug/fork — authed. Throws on 401 (caller should
 * have already redirected to signin first).
 */
export async function forkPublicBook(
  slug: string,
  chapterNumber: number,
  newStoryName?: string,
): Promise<ForkResponse> {
  const res = await authedFetch(
    `/api/public/books/${encodeURIComponent(slug)}/fork`,
    {
      method: "POST",
      body: JSON.stringify({ chapterNumber, newStoryName }),
    },
  );
  return parseOrThrow<ForkResponse>(res);
}

/**
 * Build the canonical absolute share URL for a slug. Always points at the
 * story subdomain — never at the current origin (so links shared from
 * www.* still resolve to story.*).
 */
export function buildShareUrl(slug: string): string {
  if (typeof window === "undefined") return `/s/${slug}`;
  // In prod the story-subdomain origin is the same as the current page's
  // origin. In dev the same Vite server serves both surfaces, so we just
  // use the current origin + the /s/ path.
  return `${window.location.origin}/s/${slug}`;
}

/**
 * EPUB download URL for a slug. Phase C: pass the active reader
 * language to opt into the translated EPUB. Source-language downloads
 * (no lang OR lang === originalLang) keep their pre-Phase-C URL shape
 * so the R2 cache continues serving any pre-existing artifacts.
 */
export function buildEpubUrl(slug: string, lang?: string): string {
  const base = `${API_BASE}/api/public/books/${encodeURIComponent(slug)}/epub`;
  return lang ? `${base}?lang=${encodeURIComponent(lang)}` : base;
}

/**
 * Download the EPUB as a Blob. Used by the Phase C UI so the client can
 * show a "Preparing EPUB…" → "Translating chapters…" progress affordance
 * before the browser surfaces the download. The endpoint may take a few
 * seconds for a first-time non-original-language pass (batched
 * Anthropic call) so a visible progress state matters.
 *
 * Returns a tuple of (Blob, suggestedFilename). The filename comes from
 * the server's Content-Disposition header; we fall back to "book.epub".
 */
export async function downloadEpubBlob(
  slug: string,
  lang?: string,
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(buildEpubUrl(slug, lang), {
    method: "GET",
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new PublicBooksApiError(
      text.slice(0, 200) || `HTTP ${res.status}`,
      res.status,
    );
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const m = disposition.match(/filename="([^"]+)"/);
  const filename = m ? m[1]! : "book.epub";
  return { blob, filename };
}

/** Embed widget iframe URL for a slug. */
export function buildEmbedUrl(slug: string, chapter?: number | "first" | "latest"): string {
  const c = chapter ? `?chapter=${encodeURIComponent(String(chapter))}` : "";
  return `${API_BASE}/api/public/books/${encodeURIComponent(slug)}/embed${c}`;
}
